import PageWrapper from "./PageWrapper/PageWrapper";
import { CardV2, CardContent } from "../components/CardV2/CardV2";
import Table, { ColumnDefinition } from "../components/Table/Table";
import FilterPill from "../components/FilterPill/FilterPill";
import { Autocomplete } from "../components/Autocomplete/Autocomplete";
import { useState, useEffect, useMemo, useRef } from "react";
import PrimaryButton from "../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton/SecondaryButton";
import Tooltip from "../components/Tooltip/Tooltip";
import IconButton from "../components/IconButton/IconButton";
import { Trash2, Pencil, Check, X } from "lucide-react";
import { CommissionContract } from "../api/ComissionContractsAPI";
import ComissionContractsAPI from "../api/ComissionContractsAPI";
import { useNavigate } from "react-router-dom";
import NoDataCTA from "../components/NoDataCTA/NoDataCTA";
import { CommissionStatus } from "../types/commission-statuses";
import { toastService } from "../services/ToastService";
import ConfirmationPopup from "../components/ConfirmationPopup/ConfirmationPopup";
import StoreCommissionPanel from "../components/StoreCommissionPanel/StoreCommissionPanel";
import CommissionContractSearchResult from "../components/CommissionContractSearchResult/CommissionContractSearchResult";
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor, paymentStatusToTranslationKey } from "../shared/paymentStatusToTranslationKey";
import Pill from "../components/Pill/Pill";
import { useTranslation } from "react-i18next";
import { commissionStatusToAbbreviatedTranslationKey, commissionStatusToPillColor, commissionStatusToTranslationKey } from "../utils/commission-status";
import { PaymentStatus } from "../types/payment-statuses";
import Tab from "../components/Tab/Tab";
import { Icon } from "../components/Icon/Icon";
import funnelIcon from "../assets/images/funnel.svg";
import xMarkIcon from "../assets/images/x-mark.svg";
import DropSelect from "../components/DropSelect/DropSelect";
import { 
  TAB_OPTIONS, 
  TabOption, 
  PAGE_SIZE_OPTIONS, 
  DEFAULT_PAGE_SIZE, 
  DEFAULT_PAGE_SIZE_SMALL, 
  SEARCH_RESULTS_SIZE, 
  PAYMENT_STATUSES, 
  OTHER_STATUSES, 
  PaginationInfo, 
  DEFAULT_MANUAL_COMMISSION 
} from "./CommissionTrackAllPage/constants";
import useFeatureFlag from "../hooks/usePermissionFlag";

const CommissionTrackAllPage = () => {
  const [commissions, setCommissions] = useState<CommissionContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonthDate, setSelectedMonthDate] = useState<string>('');
  const [selectedPayment, setSelectedPayment] = useState<PaymentStatus | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<CommissionStatus | undefined>(undefined);
  const [openPanel, setOpenPanel] = useState<CommissionContract | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showManualPanel, setShowManualPanel] = useState(false);
  const [manualCommission, setManualCommission] = useState<CommissionContract | null>(null);
  const [activeTab, setActiveTab] = useState<TabOption>(TAB_OPTIONS.OTHER);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalElements: 0,
    totalPages: 0,
    size: DEFAULT_PAGE_SIZE,
    number: 0
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_SMALL);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const requestIdRef = useRef(0);
  const { hasAccess: canEditCommissions } = useFeatureFlag(['edit-commission-contracts']);

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const isFiltering = !!selectedPayment || !!selectedStatus || !!selectedMonthDate;
  
  // Extract month-year from full date for API calls - memoized to prevent infinite loops
  const selectedMonth = useMemo(() => {
    const month = selectedMonthDate ? selectedMonthDate.substring(0, 7) : undefined;
    console.log('[useMemo selectedMonth] Input:', selectedMonthDate, 'Output:', month);
    return month;
  }, [selectedMonthDate]);

  const getCurrentStatuses = () => {
    return OTHER_STATUSES;
  };

  const handleDelete = async (id: number) => {
    try {
      await ComissionContractsAPI.delete(id);
      setCurrentPage(prev => prev);
      toastService.showToast(t("commission_track_all_page.toast.deleted"), "success");
    } catch (error) {
      console.error('Error deleting commission:', error);
      toastService.showToast(t("commission_track_all_page.toast.delete_error"), "error");
    }
  };

  useEffect(() => {
    const itemReceived = activeTab === TAB_OPTIONS.IN_STORE;
    const controller = new AbortController();
    const signal = controller.signal;
    const reqId = ++requestIdRef.current;
  
    setLoading(true);
  
    (async () => {
      try {
        const params = {
          month: selectedMonth || null,
          payment: selectedPayment || null,
          status: selectedStatus || null,
          itemReceived,
          search: null,
          page: currentPage,
          size: pageSize,
        };
  
        const response = await ComissionContractsAPI.getFiltered(params);
  
        if (reqId !== requestIdRef.current) return;
  
        const { content, totalElements, totalPages, size, number } = response.data;
  
        setCommissions(content);
        setPagination({ totalElements, totalPages, size, number });
      } catch (error: any) {
        if (signal.aborted) return;
        toastService.showToast(t("toast.errors.commission_contracts.load"), 'error');
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    })();
  
    return () => {
      controller.abort();
    };
  }, [
    selectedMonth,
    selectedPayment,
    selectedStatus,
    activeTab,
    currentPage,
    pageSize,
    t,
  ]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const searchFn = async (input: string, resultsSize: number, setResultsFn: any) => {
    if (!input || input.trim().length < 2) {
      setResultsFn([]);
      return;
    }

    setIsSearchLoading(true);
    try {
      const response = await ComissionContractsAPI.getFiltered({
        search: input.trim(),
        page: 0,
        size: resultsSize,
        month: null,
        payment: null,
        status: null,
        itemReceived: null,
      });
      
      setResultsFn(response.data.content);
    } catch (error) {
      console.error('Error searching commissions:', error);
      setResultsFn([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedPayment(undefined);
    setSelectedStatus(undefined);
    setSelectedMonthDate('');
    setCurrentPage(0);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedMonthDate(value || '');
    setCurrentPage(0);
  };

  // Add handler to open manual panel
  const handleManualInsert = () => {
    setManualCommission(DEFAULT_MANUAL_COMMISSION);
    setShowManualPanel(true);
  };

  // Add handler to save manual commission
  const handleSaveManualCommission = (commission: CommissionContract) => {
    setLoading(true);
    // Ensure we're sending enum values to the API
    const commissionToSend = {
      ...commission,
      status: commission.status as CommissionStatus,
      payment: commission.payment as PaymentStatus
    };
    ComissionContractsAPI.create(commissionToSend)
      .then(res => {
        // Trigger refetch by updating currentPage (which will trigger useEffect)
        setCurrentPage(prev => prev);
        setShowManualPanel(false);
        setManualCommission(null);
      })
      .finally(() => setLoading(false));
  };

  const handleUpdateCommission = (commission: CommissionContract) => {
    if (!commission.id) return;
    setLoading(true);
    const { submissionDate, ...commissionToSend } = commission;
    const commissionToUpdate = {
      ...commissionToSend,
      status: commission.status as CommissionStatus,
      payment: commission.payment as PaymentStatus
    };
    ComissionContractsAPI.update(commission.id, commissionToUpdate)
      .then(res => {
        setCommissions(prev => prev.map(c => c.id === commission.id ? res.data : c));
        setOpenPanel(null);
      })
      .finally(() => setLoading(false));
  };

  const formatEndDateWithDaysRemaining = (endDateString: string | undefined) => {
    if (!endDateString) {
      return <span>{t("commission_track_all_page.table.no_data")}</span>;
    }
    
    try {
      // Parse to local timezone for days calculation
      const dateMatch = endDateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        const endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        endDate.setHours(0, 0, 0, 0);
        
        const diffTime = endDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return (
          <span className="flex items-center">
            {endDateString}
            <span className="text-gray-500 text-sm ml-1">
              ({diffDays})
            </span>
          </span>
        );
      }
      
      const endDate = new Date(endDateString);
      if (isNaN(endDate.getTime())) {
        return <span>{endDateString}</span>;
      }
      
      endDate.setHours(0, 0, 0, 0);
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return (
        <span className="flex items-center">
          {endDateString}
          <span className="text-gray-500 text-sm ml-1">
            ({diffDays})
          </span>
        </span>
      );
    } catch (e) {
      return <span>{endDateString}</span>;
    }
  };

  const columns: ColumnDefinition<CommissionContract>[] = [
    {
      key: 'brideName',
      title: t("commission_track_all_page.table.columns.bride_name"),
      render: (row: CommissionContract) => {
        const fullName = [row.brideName, row.brideSurname].filter(Boolean).join(' ');
        return <span className="font-medium">{fullName || t("commission_track_all_page.table.no_data")}</span>;
      },
    },
    {
      key: 'submissionDate',
      title: t("commission_track_all_page.table.columns.submission_date"),
      render: (row: CommissionContract) => {
        if (!row.submissionDate) return <span className="font-medium">{t("commission_track_all_page.table.no_data")}</span>;
        try {
          // Parse date string directly to avoid timezone issues
          // If the string is in YYYY-MM-DD format, use it directly
          const dateMatch = row.submissionDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            return <span className="font-medium">{row.submissionDate}</span>;
          }
          // Fallback for other date formats
          const date = new Date(row.submissionDate);
          return <span className="font-medium">{date.toISOString().split('T')[0]}</span>;
        } catch (e) {
          return <span className="font-medium">{row.submissionDate}</span>;
        }
      },
    },
    {
      key: 'status',
      title: t("commission_track_all_page.table.columns.status"),
      render: (row: CommissionContract) => {
        return (
          <Tooltip content={t(commissionStatusToTranslationKey(row.status as CommissionStatus))}>
            <Pill 
              text={t(commissionStatusToAbbreviatedTranslationKey(row.status as CommissionStatus))} 
              color={commissionStatusToPillColor(row.status as CommissionStatus)}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'payment',
      title: t("commission_track_all_page.table.columns.payment_status"),
      render: (row: CommissionContract) => {
        return (
          <Tooltip content={t(paymentStatusToTranslationKey(row.payment as PaymentStatus))}>
            <Pill 
              text={t(paymentStatusToAbbreviatedTranslationKey(row.payment as PaymentStatus))} 
              color={paymentStatusToPillColor(row.payment as PaymentStatus)}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'itemReceived',
      title: t("commission_track_all_page.table.columns.item_received"),
      render: (row: CommissionContract) => {
        return (
          <div className="flex justify-center">
            {row.itemReceived ? (
              <Check className="h-5 w-5 text-green-600" />
            ) : (
              <X className="h-5 w-5 text-red-600" />
            )}
          </div>
        );
      },
    },
    {
      key: 'dress',
      title: t("commission_track_all_page.table.columns.dress_model"),
      render: (row: CommissionContract) => row.dress || t("commission_track_all_page.table.no_data"),
    },
    {
      key: 'prices',
      title: t("commission_track_all_page.table.columns.prices"),
      render: (row: CommissionContract) => {
        const formatPrice = (amount?: number, currency?: string) => {
          if (!amount) return t("commission_track_all_page.table.no_data");
          return `${amount.toLocaleString('pl-PL')} ${currency || 'PLN'}`;
        };

        const purchasePrice = formatPrice(row.purchasePriceAmount, row.purchasePriceCurrency);
        const desiredPrice = formatPrice(row.desiredSalePriceAmount, row.desiredSalePriceCurrency);

        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">{t("commission_track_all_page.table.prices.purchase")}: {purchasePrice}</span>
            <span className="text-sm text-muted-foreground">{t("commission_track_all_page.table.prices.sale")}: {desiredPrice}</span>
          </div>
        );
      },
    },
    {
      key: 'endDate',
      title: t("commission_track_all_page.table.columns.end_date"),
      render: (row: CommissionContract) => formatEndDateWithDaysRemaining(row.endDate),
    },
    {
      key: 'actions' as any,
      title: '',
      render: (row: CommissionContract) => (
        <div className="flex items-center gap-2">
          <IconButton
            onClick={() => {
              setOpenPanel(row);
              setIsEditMode(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </IconButton>
          <IconButton
            onClick={() => {}}
            disabled={row.status === 'SOLD'}
          >
            <ConfirmationPopup 
              onConfirm={() => row.id && handleDelete(row.id)} 
              confirmText={t("commission_track_all_page.delete_confirm.text") || "Are you sure you want to delete this commission?"}
              title={t("commission_track_all_page.delete_confirm.title") || "Delete commission"}
            >
              <Trash2 className="h-4 w-4" />
            </ConfirmationPopup>
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <PageWrapper>
      <div className="flex flex-col w-full space-y-5">
        <CardV2 className="w-full pt-4">
          <CardContent>
            {/* Search row */}
            <div className="flex-col">
              <div className="md:flex md:flex-row md:items-center md:space-x-3 mb-4">
                <div className="flex items-center space-x-3 mb-2 md:mb-0">
                  <DropSelect compact onSelect={handleMonthChange} value={selectedMonthDate} />
                </div>
                <div className="flex-1 min-w-[200px] ml-0.5 md:ml-1">
                  <Autocomplete
                  resultsSize={SEARCH_RESULTS_SIZE}
                  searchFn={searchFn}
                  onSelect={() => {}}
                  placeholder={t("commission_track_all_page.search.placeholder") || "Search by name, surname, model, contract number or reference"}
                  isLoading={isSearchLoading}
                  errorMessage={t("commission_track_all_page.search.no_results") || "No results"}
                  toElementFn={(row: CommissionContract) => (
                    <CommissionContractSearchResult 
                      key={row.id} 
                      commission={row} 
                      onView={(commission) => {
                        setOpenPanel(commission);
                        setIsEditMode(canEditCommissions);
                      }} 
                    />
                  )}
                />
                </div>
                <div className="hidden md:flex ml-4">
                <SecondaryButton 
                  text={<span className="inline-flex items-center">{t("filters.button") || "Filters"}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                  fn={() => setShowFilters(!showFilters)}
                  icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                />
                </div>
              </div>
            </div>
            <span className="md:hidden mb-4">
              <SecondaryButton 
                text={<span className="inline-flex items-center">{t("filters.button") || "Filters"}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                fn={() => setShowFilters(!showFilters)}
                icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
              />
            </span>
            {showFilters && (
              <>
                <div className="space-y-2 mb-4">
                  {/* Payment Status Section */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">{t("commission_track_all_page.filters.payment_status.title") || "Payment Status"}</h3>
                    <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                      {PAYMENT_STATUSES.map((status) => (
                        <FilterPill
                          key={status}
                          text={t(paymentStatusToTranslationKey(status))}
                          isActive={selectedPayment === status}
                          fn={() => {
                            setSelectedPayment(selectedPayment === status ? undefined : status);
                            setCurrentPage(0);
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Commission Status Section */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">{t("commission_track_all_page.filters.commission_status.title") || "Commission Status"}</h3>
                    <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                      {getCurrentStatuses().map((status) => (
                        <FilterPill
                          key={status}
                          text={t(commissionStatusToTranslationKey(status))}
                          isActive={selectedStatus === status}
                          fn={() => {
                            setSelectedStatus(selectedStatus === status ? undefined : status);
                            setCurrentPage(0);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="flex-col md:flex md:flex-row justify-between md:justify-start items-center space-x-4 mb-4">
              <PrimaryButton text={t("commission_track_all_page.add_manual_button") || "Add commission manually"} fn={handleManualInsert} />
              {isFiltering && (
                <SecondaryButton 
                  actionName="Reset Filters" 
                  text={t("filters.reset")!} 
                  fn={resetFilters}
                  icon={
                    <span className="relative inline-block w-4 h-4">
                      <Icon url={funnelIcon} cssStyles="w-4 h-4" />
                      <span className="absolute -top-1 -right-1">
                        <Icon url={xMarkIcon} cssStyles="w-3 h-3" />
                      </span>
                    </span>
                  }
                  compact
                />
              )}
            </div>
          </CardContent>
        </CardV2>
        <CardV2 className="w-full pt-4">
          <CardContent>
            {/* Tabs section */}
            <div className="flex mb-4">
              <Tab 
                fn={() => {
                  setActiveTab(TAB_OPTIONS.OTHER);
                  setCurrentPage(0);
                }} 
                label={t("commission_track_all_page.tabs.other") || "Other"} 
                isActive={activeTab === TAB_OPTIONS.OTHER}
              />
              <Tab 
                fn={() => {
                  setActiveTab(TAB_OPTIONS.IN_STORE);
                  setCurrentPage(0);
                }} 
                label={t("commission_track_all_page.tabs.in_store") || "In Store"} 
                isActive={activeTab === TAB_OPTIONS.IN_STORE}
              />
            </div>
            <div className="w-full">
              <Table<CommissionContract>
                data={commissions}
                columns={columns}
                selectedItems={[]}
                compareFunction={() => false}
                isLoading={loading}
                emptyListComponent={
                  <NoDataCTA
                    headline={t("commission_track_all_page.empty_state.headline") || "No commission submissions"}
                    subtext={t("commission_track_all_page.empty_state.subtext") || "You don't have any commission submissions yet. You can add a submission manually or create a form so brides can submit their dresses."}
                    primaryActionText={t("commission_track_all_page.empty_state.primary_action") || "Create form for brides"}
                    onPrimaryAction={() => navigate('/commission/settings')}
                    secondaryActionText={t("commission_track_all_page.empty_state.secondary_action") || "Add commission manually"}
                    onSecondaryAction={handleManualInsert}
                  />
                }
                onRowClick={row => setOpenPanel(row)}
              />
            </div>
            {/* Pagination controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
              {/* Page size selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {t("global.pagination.show", "Show")}:
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-600">
                  {t("global.pagination.per_page", "per page")}
                </span>
              </div>
              
              {/* Pagination buttons */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <SecondaryButton
                    text={t("global.previous", "Prev") as string}
                    fn={() => handlePageChange(Math.max(0, pagination.number - 1))}
                    isDisabled={pagination.number === 0}
                    actionName="Pagination Prev"
                  />
                  <span className="px-3 py-1 mx-1 text-sm text-gray-600">
                    {t("global.pagination.page", "Page {{page}} of {{totalPages}}", { 
                      page: pagination.number + 1, 
                      totalPages: pagination.totalPages 
                    })}
                  </span>
                  <SecondaryButton
                    text={t("global.next", "Next") as string}
                    fn={() => handlePageChange(Math.min(pagination.totalPages - 1, pagination.number + 1))}
                    isDisabled={pagination.number >= pagination.totalPages - 1}
                    actionName="Pagination Next"
                  />
                </div>
              )}
            </div>
            {openPanel && (
              <StoreCommissionPanel 
                commission={openPanel} 
                onClose={() => {
                  setOpenPanel(null);
                  setIsEditMode(false);
                }} 
                isEdit={isEditMode}
                onSave={handleUpdateCommission}
              />
            )}
            {/* Manual insert modal using StoreCommissionPanel in edit mode */}
            {showManualPanel && manualCommission && (
              <StoreCommissionPanel
                commission={manualCommission}
                onClose={() => setShowManualPanel(false)}
                isEdit
                onSave={handleSaveManualCommission}
              />
            )}
          </CardContent>
        </CardV2>
      </div>
    </PageWrapper>
  );
};

export default CommissionTrackAllPage; 